import React, { useEffect } from 'react'
import { SEARCH_PAGE } from '@chegg-wt/core/constants/citationFlow'
import { spacing } from '@chegg-ui/theme'
import styled from 'styled-components'
import { useRouter } from 'next/router'
import { AppState } from '@chegg-wt/core/state'
import { connect, useSelector } from 'react-redux'
import {
  origins,
  Region,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { Dispatch } from 'redux'
import BreadcrumbData from '@chegg-wt/core/components/Head/BreadcrumbData'
import { defaultSourceType, sourceTypeById } from '@chegg-wt/core/utils/sourceTypes'
import { AppRoute, AppRouteOpts, SourceType } from '@chegg-wt/core/types/core'
import { navigated } from '@chegg-wt/core/state/app/actions'
import { fetchUrlStyleName } from '@chegg-wt/core/state/currentProject/actions'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import { createCitationQuerySelector } from '@chegg-wt/core/state/createCitation/selectors'
import { currentUrlStyleNameSelector } from '@chegg-wt/core/state/currentProject/selectors'
import { updateMessagingFlagSelector } from '@chegg-wt/core/state/app/selectors'
import { SSRProps } from '@chegg-wt/core/types/layout'
import { Config } from '@chegg-wt/core/config'
import { CM } from '@chegg-wt/core/constants/site'
import { useRoutesContext } from '../RoutesContext'
import CitationSearchInput from '../CitationSearchInput'
import { PageHeader, MainWrapper, PageHeaderWrapper } from '../Layouts/styled'
import { BreadcrumbPart, Breadcrumbs } from '../Breadcrumbs'
import Credibility from '../Credibility'
import ErrorContainer from '../ErrorContainer'
import SeoContent from '../SeoContent'
import UpgradeAdFrame from '../UpgradeAdFrame'
import { SubHeadingBCRest } from '../BrandConsolidation'
import { Copy, DispatchProps, PageStructureProps, QueryParams, StateProps } from './types'
import * as Styled from './styled'

export const Container = styled.div`
  padding: ${spacing.px24} calc(${spacing.px24} + ${spacing.px4}) ${spacing.px24}
    ${spacing.px24};
`

const SEOcopy = (copy: Copy) => {
  if (!copy) return null
  if (copy.seoContent)
    return <SeoContent content={copy.seoContent} advertPlacement="C2" />
  return <Credibility copy={copy.considerCredibility} advertPlacement="C2" />
}

export const SourceSearchPageStructureTop: React.FC<Partial<SSRProps>> = ({
  initialUrlStyleName,
  isDefaultStyle,
  currentSourceType,
}) => {
  const updateMessagingFeatureEnabled = useSelector(updateMessagingFlagSelector)
  const { appName } = Config
  return (
    <PageHeaderWrapper>
      <PageHeader>
        {Config.styleNotInSourceSearchPageUrl
          ? `Reference ${
              sourceTypeById(currentSourceType)?.title ?? `a ${currentSourceType}`
            }`
          : `Cite ${
              sourceTypeById(currentSourceType)?.title ?? `a ${currentSourceType}`
            }${isDefaultStyle ? '' : ` in ${initialUrlStyleName}`}`}
      </PageHeader>
      {(appName !== CM || updateMessagingFeatureEnabled) && <SubHeadingBCRest />}
    </PageHeaderWrapper>
  )
}

export const SourceSearchPageStructure: React.FC<
  PageStructureProps & DispatchProps & StateProps
> = ({
  currentStyle,
  styleId,
  urlStyleName,
  initialSourceType,
  initialUrlStyleName,
  currentSourceType,
  selectSourceType,
  copy,
  urlStyleNameFetched,
  navigated,
  setSearchQuery,
  onCreateManualCitationClickCTA,
}) => {
  const routes = useRoutesContext()
  const router = useRouter()
  const { searchQuery: initialQuery = '' } = router.query as QueryParams

  const sourceType =
    sourceTypeById(currentSourceType) ||
    sourceTypeById(initialSourceType) ||
    defaultSourceType

  const hasBreadcrumbs =
    typeof styleId === 'string' && typeof initialUrlStyleName === 'string'

  useEffect(() => {
    if (hasBreadcrumbs) urlStyleNameFetched(styleId)
  }, [styleId])

  const breadcrumbs: BreadcrumbPart[] = [
    {
      label: 'Home',
      route: routes.toCitationsHome(),
    },
    {
      label: `${urlStyleName || initialUrlStyleName} Citation Generator`,
      route: routes.toSourceTypeSelection({ style: styleId }),
    },
    {
      label: `Cite ${sourceTypeById(initialSourceType)?.title}`,
      route: routes.toSourceSearch({
        style: styleId,
        sourceType: sourceTypeById(initialSourceType)?.value,
      }),
      last: true,
    },
  ]

  const handleManualCitationClick = (region: Region) => {
    navigated(
      routes.toManualCitation({ style: currentStyle, sourceType: sourceType.value }),
      { replace: true }
    )
    onCreateManualCitationClickCTA(region)
  }

  const onSourceTypeSelect = ({ id: sourceType }: SourceType) => {
    const previousSourceType = initialSourceType || currentSourceType
    if (sourceType !== previousSourceType) {
      selectSourceType(sourceType, 'toSourceSearch')
    }
  }

  const { toManualCitation, toSourceSearch } = useRoutesContext()
  const composeSourceTypeLink = (sourceType: SourceType): AppRoute => {
    const routeParams = { style: currentStyle || styleId, sourceType: sourceType.id }
    if (sourceType.manual) {
      return toManualCitation({ ...routeParams })
    }
    return toSourceSearch({ ...routeParams })
  }

  return (
    <>
      {hasBreadcrumbs ? <BreadcrumbData breadcrumbs={breadcrumbs} /> : null}
      <Styled.PageContainer>
        <UpgradeAdFrame
          copy={copy?.upgradeCopy}
          page={SEARCH_PAGE}
          upgradePromotionCopy={copy?.upgradePromotionCopy}
        >
          <ErrorContainer />
          <MainWrapper>
            <CitationSearchInput
              composeRoute={composeSourceTypeLink}
              sourceType={sourceType}
              initialSearchQuery={initialQuery}
              onChangeSourceType={onSourceTypeSelect}
              onChangeSearchQuery={setSearchQuery}
              onManualCiteClick={handleManualCitationClick}
            />
          </MainWrapper>
        </UpgradeAdFrame>
        {SEOcopy(copy)}
        {hasBreadcrumbs ? <Breadcrumbs parts={breadcrumbs} divider=">" /> : null}
      </Styled.PageContainer>
    </>
  )
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  urlStyleNameFetched: (style: string) => dispatch(fetchUrlStyleName(style)),
  navigated: (route: AppRoute, opts?: AppRouteOpts) => dispatch(navigated(route, opts)),
  onCreateManualCitationClickCTA: (region: Region) =>
    dispatch(userClick({ origin: origins.createManualCitationCTA, region })),
})

const mapStateToProps = (state: AppState) => ({
  sourceSearchQuery: createCitationQuerySelector(state),
  urlStyleName: currentUrlStyleNameSelector(state),
})

export default connect<StateProps, DispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(SourceSearchPageStructure)
